import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ThemeToggle } from '../components/ThemeToggle'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'

export function TermsOfService() {
  const { lang } = useParams<{ lang: string }>()
  const { t } = useTranslation('legal')

  useEffect(() => {
    if (lang) {
      localStorage.setItem('i18nextLng', lang)
    }
  }, [lang])

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const currentLang = lang || 'en'

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted to-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle />
      </div>

      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="mb-8">
            <Link
              to={`/${currentLang}`}
              className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-6 transition-colors duration-200"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              {t('terms.backToHome')}
            </Link>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
              {t('terms.title')}
            </h1>
            <p className="mt-4 text-muted-foreground">
              {t('terms.lastUpdated')}
            </p>
          </div>

          {/* Content Card */}
          <motion.div
            className="bg-card rounded-2xl shadow-xl p-8 space-y-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Agreement to Terms */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('terms.agreement.title')}
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                {t('terms.agreement.text')}
              </p>
            </section>

            {/* Changes to Terms */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('terms.changes.title')}
              </h2>
              <p className="text-muted-foreground">
                {t('terms.changes.text')}
              </p>
            </section>

            {/* Description of Service */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('terms.description.title')}
              </h2>
              <p className="text-muted-foreground mb-3">
                {t('terms.description.intro')}
              </p>
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border-l-4 border-amber-500 mt-4">
                <p className="text-muted-foreground text-sm">
                  {t('terms.description.disclaimer')}
                </p>
              </div>
            </section>

            {/* User Conduct and Responsibilities */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('terms.userConduct.title')}
              </h2>
              <p className="text-muted-foreground mb-3">
                {t('terms.userConduct.intro')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>{t('terms.userConduct.lawful')}</li>
                <li>{t('terms.userConduct.comply')}</li>
                <li>{t('terms.userConduct.noUnauthorizedAccess')}</li>
                <li>{t('terms.userConduct.noDisrupt')}</li>
                <li>{t('terms.userConduct.noMalicious')}</li>
                <li>{t('terms.userConduct.noSpam')}</li>
                <li>{t('terms.userConduct.respectIP')}</li>
                <li>{t('terms.userConduct.noReverseEngineer')}</li>
              </ul>

              <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border-l-4 border-red-500 mt-6">
                <h4 className="text-lg font-semibold text-foreground mb-2">
                  {t('terms.userConduct.prohibitedActivities.title')}
                </h4>
                <p className="text-muted-foreground text-sm">
                  {t('terms.userConduct.prohibitedActivities.text')}
                </p>
              </div>
            </section>

            {/* Intellectual Property Rights */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('terms.intellectualProperty.title')}
              </h2>

              <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">
                {t('terms.intellectualProperty.ourIP.title')}
              </h3>
              <p className="text-muted-foreground">
                {t('terms.intellectualProperty.ourIP.text')}
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">
                {t('terms.intellectualProperty.yourContent.title')}
              </h3>
              <p className="text-muted-foreground">
                {t('terms.intellectualProperty.yourContent.text')}
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">
                {t('terms.intellectualProperty.materials.title')}
              </h3>
              <p className="text-muted-foreground">
                {t('terms.intellectualProperty.materials.text')}
              </p>
            </section>

            {/* Privacy and Data Protection */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('terms.privacy.title')}
              </h2>
              <p className="text-muted-foreground">
                {t('terms.privacy.text')}
              </p>
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 border-l-4 border-blue-500 mt-4">
                <h4 className="text-lg font-semibold text-foreground mb-2">
                  {t('terms.privacy.gdprCompliance.title')}
                </h4>
                <p className="text-muted-foreground text-sm">
                  {t('terms.privacy.gdprCompliance.text')}
                </p>
              </div>
            </section>

            {/* Termination of Service */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('terms.termination.title')}
              </h2>
              <p className="text-muted-foreground">
                {t('terms.termination.text')}
              </p>
            </section>

            {/* Disclaimers and Warranties */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('terms.disclaimers.title')}
              </h2>
              <p className="text-muted-foreground mb-3">
                {t('terms.disclaimers.intro')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>{t('terms.disclaimers.merchantability')}</li>
                <li>{t('terms.disclaimers.fitness')}</li>
                <li>{t('terms.disclaimers.nonInfringement')}</li>
                <li>{t('terms.disclaimers.uninterrupted')}</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                {t('terms.disclaimers.noGuarantee')}
              </p>
            </section>

            {/* Limitation of Liability */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('terms.limitation.title')}
              </h2>
              <p className="text-muted-foreground mb-3">
                {t('terms.limitation.intro')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>{t('terms.limitation.indirect')}</li>
                <li>{t('terms.limitation.loss')}</li>
                <li>{t('terms.limitation.exceeding')}</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                {t('terms.limitation.jurisdiction')}
              </p>
            </section>

            {/* Indemnification */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('terms.indemnification.title')}
              </h2>
              <p className="text-muted-foreground">
                {t('terms.indemnification.intro')}
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4 mt-3">
                <li>{t('terms.indemnification.use')}</li>
                <li>{t('terms.indemnification.violation')}</li>
                <li>{t('terms.indemnification.thirdParty')}</li>
                <li>{t('terms.indemnification.content')}</li>
              </ul>
            </section>

            {/* Governing Law and Dispute Resolution */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('terms.governingLaw.title')}
              </h2>
              <p className="text-muted-foreground mb-3">
                {t('terms.governingLaw.text')}
              </p>
              <p className="text-muted-foreground">
                {t('terms.governingLaw.amicable')}{' '}
                <a
                  href="mailto:support@securesight.ai"
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  support@securesight.ai
                </a>
                .
              </p>
            </section>

            {/* General Provisions */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('terms.general.title')}
              </h2>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>
                  <strong className="text-foreground">
                    {t('terms.general.entireAgreement')}
                  </strong>{' '}
                  {t('terms.general.entireAgreementValue')}
                </li>
                <li>
                  <strong className="text-foreground">
                    {t('terms.general.severability')}
                  </strong>{' '}
                  {t('terms.general.severabilityValue')}
                </li>
                <li>
                  <strong className="text-foreground">
                    {t('terms.general.waiver')}
                  </strong>{' '}
                  {t('terms.general.waiverValue')}
                </li>
                <li>
                  <strong className="text-foreground">
                    {t('terms.general.assignment')}
                  </strong>{' '}
                  {t('terms.general.assignmentValue')}
                </li>
              </ul>
            </section>

            {/* Contact Information */}
            <section className="bg-muted rounded-xl p-6">
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('terms.contact.title')}
              </h2>
              <p className="text-muted-foreground mb-3">
                {t('terms.contact.intro')}
              </p>
              <div className="space-y-2 text-muted-foreground">
                <p>
                  <strong className="text-foreground">
                    {t('terms.contact.email')}
                  </strong>{' '}
                  <a
                    href="mailto:info@securesight.ai"
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    info@securesight.ai
                  </a>
                </p>
                <p>
                  <strong className="text-foreground">
                    {t('terms.contact.address')}
                  </strong>{' '}
                  <br />
                  {t('terms.contact.addressValue')}
                </p>
              </div>
            </section>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}
